import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  // rounded-[var(--radius-control)] matches the 26BET design kit's .bet-btn
  // exactly (border-radius: var(--radius-control)) instead of shadcn's
  // generic rounded-md.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap !rounded-[var(--radius-control)] text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        // 26BET design-kit variants — token-driven, additive (existing
        // default/destructive/link callers above are untouched).
        outline:
          "!border !border-[var(--gold-500)] bg-transparent text-[var(--gold-400)] hover:bg-[var(--gold-a12)] hover:text-[var(--gold-300)]",
        secondary:
          "!border !border-[var(--border-default)] bg-[var(--surface-3)] text-[var(--text-primary)] hover:bg-[var(--bet-mobile-track)]",
        ghost:
          "text-[var(--text-secondary)] hover:bg-[var(--surface-3)] hover:text-[var(--text-primary)]",
        link: "text-primary underline-offset-4 hover:underline",
        primary: "bg-[var(--gold-500)] text-[var(--on-brand)] hover:bg-[var(--gold-400)] active:bg-[var(--gold-600)]",
        danger: "bg-[var(--red-500)] text-white hover:opacity-90",
        success: "bg-[var(--green-500)] text-[var(--on-brand)] hover:opacity-90",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        // 26BET design-kit scale (xs/md alongside the existing sm/default/lg)
        xs: "h-7 gap-1 px-2.5 text-xs has-[>svg]:px-2",
        md: "h-[42px] px-5 has-[>svg]:px-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...(props as React.ComponentPropsWithoutRef<"button">)}
    />
  )
}

export { Button, buttonVariants }
